import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "@/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { stepChanged, selectedZipCodeChanged, siteSelected, slotReserved } from "@/app/booking-flow/publicBookingFlowSlice";
import type { SlotReservation } from "@/app/booking-flow/publicBookingFlowTypes";
import { useReserveForZip } from "../useReserveForZip";
import { AlternateSitePrompt } from "./AlternateSitePrompt";
import { CalThrottledNotice } from "@/components/CalThrottledNotice";

const ZIP_CODE_REGEX = /^\d{5}$/;

export const ZipEntryStep = () => {
  const savedZipCode = useAppSelector(state => state.publicBookingFlow.selectedZipCode);
  const dispatch = useAppDispatch();

  const [zipCodeInput, setZipCodeInput] = useState<string>(savedZipCode || "");
  const [statusText, setStatusText] = useState("");
  const [zipError, setZipError] = useState("");

  // A hold — accepted, if it is at an alternate site — moves the flow on to step 2.
  const handleReserved = useCallback((zipCode: string, slotReservation: SlotReservation) => {
    dispatch(selectedZipCodeChanged(zipCode));
    dispatch(siteSelected(slotReservation.site));
    dispatch(slotReserved(slotReservation));
    dispatch(stepChanged(2));
  }, [dispatch]);

  const { reserve, loading, pendingSite, approvePendingSite, declinePendingSite, throttled, retry } = useReserveForZip(handleReserved);

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 5);
    setZipCodeInput(digitsOnly);
    if (zipError != "") setZipError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!ZIP_CODE_REGEX.test(zipCodeInput)) {
      setZipError("Please enter a valid 5-digit ZIP code.");
      return;
    }

    setStatusText("Checking service area and appointment availability…");
    await reserve(zipCodeInput);
  };

  if (pendingSite) {
    return <AlternateSitePrompt site={pendingSite} onApprove={approvePendingSite} onDecline={declinePendingSite} />;
  }

  return (
    <Card className="w-full max-w-xl mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
      <CardHeader className="text-center px-4">
        <CardTitle className="text-2xl font-semibold text-slate-900">Get Started</CardTitle>
        <CardDescription className="text-slate-500">
          Enter your ZIP code to check service area and appointment availability.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form id="zip-entry-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5 max-w-md mx-auto">
            <Label htmlFor="zipCode">ZIP Code</Label>
            <Input
              required
              id="zipCode"
              name="zipCode"
              placeholder="e.g. 98003"
              value={zipCodeInput}
              onChange={handleZipChange}
              inputMode="numeric"
              pattern="\d{5}"
              maxLength={5}
              aria-invalid={zipError != ""}
              aria-describedby={zipError ? "zipCode-error" : undefined}
            />
            {zipError != "" && (
              <p id="zipCode-error" className="text-sm text-red-600">
                {zipError}
              </p>
            )}
          </div>
        </form>
        {throttled != null && (
          <CalThrottledNotice
            variant="public"
            error={throttled}
            onRetry={retry}
            busy={loading}
            autoRetry
            className="mt-4 max-w-md mx-auto"
          />
        )}
      </CardContent>

      <CardFooter className="px-4 pb-6 flex flex-col gap-2 w-full max-w-md mx-auto">
        <Button form="zip-entry-form" type="submit" className="w-full text-base h-12" disabled={loading || zipCodeInput.length !== 5}>
          {loading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {statusText}
            </span>
          ) : (
            "Continue"
          )}
        </Button>
        <Button asChild variant="ghost" className="w-full text-slate-400">
          <Link to="/booking">Back to Scheduler Home</Link>
        </Button>
      </CardFooter>
    </Card>
  );
}
